import React, { useRef, useState } from "react";
import { Space, Switch } from "antd";

import { useAppContext } from "@components/AppContext";
import {
  enableAccount2FAOperation as enableOperation,
  disableAccount2FAOperation as disableOperation,
  disableMyAccount2FAOperation as disableMyOperation,
} from "@api/services/investments";
import TwoFactorAuthenticationModal from "../TwoFactorAuthenticationModal";

const LABEL_ENABLED = "Enabled";
const LABEL_DISABLED = "Disabled";

type ModalControls = {
  open?: () => void;
};

type Props = {
  mobileNumber?: string;
  accountId?: string;
  mfaMethods?: string[];
  isDisabled?: boolean;
  isEnabled: boolean;
  onSuccess: (shouldEnable: boolean) => Promise<void> | void;
};

type AppContextValue = {
  request: <T = unknown>(
    operation: unknown,
    parameters?: Record<string, unknown>
  ) => Promise<T>;
  isImpersonated?: boolean;
};

const TwoFactorAuthenticationSwitch = ({
  mobileNumber,
  accountId,
  mfaMethods = [],
  isDisabled = false,
  isEnabled,
  onSuccess,
}: Props) => {
  const modal = useRef<ModalControls>({});

  const [ isLoading, setIsLoading ] = useState(false);
  const { request, isImpersonated } = useAppContext() as AppContextValue;

  const enable = () =>
    request(enableOperation, { id: accountId });

  const disable = () =>
    accountId
      ? request(disableOperation, { id: accountId })
      : request(disableMyOperation);

  const onChange = async (shouldEnable: boolean) => {
    const isSelfService = !accountId;
    const shouldOpenMethodModal = shouldEnable && isSelfService;

    if (shouldOpenMethodModal) {
      modal.current.open?.();
      return;
    }

    setIsLoading(true);

    if (shouldEnable) {
      await enable();
    } else {
      await disable();
    }

    await onSuccess(shouldEnable);
    setIsLoading(false);
  };

  const onVerificationSuccess = async () => {
    setIsLoading(true);
    await onSuccess(true);
    setIsLoading(false);
  };

  const shouldDisableSwitch = isDisabled || Boolean(isImpersonated);

  return (
    <>
      <Space direction="vertical">
        <Switch
          checked={isEnabled}
          loading={isLoading}
          onChange={onChange}
          disabled={shouldDisableSwitch}
          checkedChildren={LABEL_ENABLED}
          unCheckedChildren={LABEL_DISABLED}
        />
      </Space>

      <TwoFactorAuthenticationModal
        modal={modal}
        mobileNumber={mobileNumber}
        mfaMethods={mfaMethods}
        isDisabled={isLoading || shouldDisableSwitch}
        onSuccess={onVerificationSuccess}
      />
    </>
  );
};

export default TwoFactorAuthenticationSwitch;
